<template>
  <div class="jps-map" v-resize="resize" ref="root">
    <div class="viewer" ref="leaflet"></div>
  </div>
</template>

<script lang="ts" setup>
import vResize from '~/directives/resize-observer'

export interface MapPoint {
  lat: number
  lon: number
  label: string
  id: string
  image?: string
}

interface Props {
  center?: LatLon
  zoom?: number
  points: MapPoint[]
  mode?: 'NORMAL' | 'MERGE'
  circleParam?: {
    lat: number
    lon: number
    radius: number
  }
}
const props = withDefaults(defineProps<Props>(), {
  center: { lat: 35.3622222, lon: 138.7313889 } as LatLon,
  zoom: 5,
  mode: 'MERGE'
})

const {
  $createLeafletMap,
  $createLeafletLayerGroup,
  $setLeafletTileLayer,
  $createLeafletCircle,
  $createLeafletMarkerClusterGroup,
  $createLeafletIcon,
  $createLeafletMarker,
} = useNuxtApp()

const emits = defineEmits(['click-map', 'move', 'click-point'])

////vueの管理外
let map: any // L.Map
let circle: any // L.Circle

const leaflet = ref<HTMLElement>(null)
onMounted(async () => {

  map = $createLeafletMap(leaflet.value, {})
  $setLeafletTileLayer(map, 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png', {
    attribution: `<a href="https://maps.gsi.go.jp/development/ichiran.html#pale" target="_blank" rel="noopener">国土地理院ウェブサイト</a>`,
  })
  map.doubleClickZoom.disable()
  map.on('click', (e: any) => {
    emits('click-map', e.latlng)
  })
  circle = $createLeafletCircle(map, [0, 0], {
    radius: 0,
    color: '#196ac9',
    fillOpacity: 0,
  })
  watchRadius()

  map.on('move', (e) => emits('move', getView()))
  map.on('zoomend', (e) => emits('move', getView()))

  setView()
  watchPoints()
})

const resize = () => {
  nextTick(() => {
    if (map) {
      map.invalidateSize()
    }
  })
}

watch(
  ()=> props.center,
  () => setView(),
)

const watchRadius = () => {
  if (circle) {
    if (props.circleParam) {
      circle.setLatLng({
        lat: props.circleParam.lat,
        lng: props.circleParam.lon,
      })
      circle.setRadius(props.circleParam.radius * 1000)
    } else {
      circle.setRadius(-1)
    }
  }
}

watch(
  ()=> props.circleParam,
  () => watchRadius(),
)

const setView = () => {
  if (props.center) {
    map.setView([props.center.lat, props.center.lon], props.zoom)
  } else {
    map.setView([35.3622222, 138.7313889], 5)
  }
}

watch(
  ()=> props.zoom,
  () => setView(),
)

let markerLayer: any // L.LayerGroup
const watchPoints = () => {
  if (markerLayer) {
    map.removeLayer(markerLayer)
  }
  if (props.points) {
    markerLayer =
      props.mode === 'MERGE'
        ? $createLeafletMarkerClusterGroup({
            maxClusterRadius: 40,
            spiderLegPolylineOptions: {
              weight: 1.5,
              color: '#222',
              opacity: 0,
            },
          })
        : $createLeafletLayerGroup()
    props.points.forEach((point, index) => {
      let marker = null

      if (point.image) {
        const myIcon = $createLeafletIcon({
          iconUrl: point.image,
          iconSize: [50, 50],
        })
        marker = $createLeafletMarker([point.lat, point.lon], {
          icon: myIcon,
        })
      } else {
        marker = $createLeafletMarker([point.lat, point.lon], {
          icon: $createLeafletIcon({
            iconUrl: getFullPath('assets/img/icon/map-marker.png'),
            iconSize: [18, 18],
            iconAnchor: [9, 18],
            tooltipAnchor: [0, -11],
          }),
        })
      }
      marker.bindTooltip(point.label)
      marker.on('click', () => {
        emits('click-point', { id: point.id, index })
      })
      marker.addTo(markerLayer)
    })
    markerLayer.addTo(map)
  }
}

watch(
  () => props.points,
  () => watchPoints(),
  { deep: true }
)

const root = ref<HTMLElement>(null)
const getView = () => {
  return {
    latlon: {
      lat: map.getCenter().lat,
      lon: map.getCenter().lng,
    },
    zoom: map.getZoom(),
    element: root.value,
  }
}
defineExpose({ getView })
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.jps-map {
  .viewer {
    height: 100%;
    margin: 0;
  }
  $shadow-color: $white;
  .jps-map-icon {
    color: $jps-key-blue;
    /* filter: drop-shadow(0px 0px 2px rgba(255, 255, 255, 0.5)); */
    /* border: 1px solid red; */
    line-height: 24px;
    /* text-shadow: 1px 1px 1px $shadow-color, -1px 1px 1px $shadow-color, */
    /*   1px -1px 1px $shadow-color, -1px -1px 1px $shadow-color, */
    /*   1px 0px 1px $shadow-color, 0px 1px 1px $shadow-color, */
    /*   -1px 0px 1px $shadow-color, 0px -1px 1px $shadow-color; */
  }
}
</style>
